'use strict';

const { bindGA, sendEventGA, normalizePayload, getText } = require('./util/analytics-util');
const { hasClass, queryFirst, queryAll, getProductData } = require('lilly/domUtil');
const { getNestedValue } = require('lilly/util');
const { BOLT_INTERACTION, BOLT_SIGN_OUT, BOLT_MODAL_CLOSE, BOLT_MODAL_PROCEED, LOYALTY_REWARDS_EVENT_NAME, ADD_TO_CART, REMOVE_FROM_CART } = require('./util/constants');
const { utag_data: uData = {} } = window;

const ECOMMERCE = 'ecommerce';
const CHECKOUT = 'checkout';
const CHECKOUT_OPTION = 'checkout option';
const SUBMISSIONS = 'submissions';
const LOYALTY_REWARDS = 'loyalty rewards';
const APPLY_REWARDS = 'apply rewards';
const APPLY = 'apply';
const REMOVE = 'remove';
const EXPAND_BTN = 'expand';
const COLLAPSE_BTN = 'collapse';
const PAYMENT_METHOD = 'payment method';
const ADD_TO_CART_UA = 'add to cart';
const REMOVE_FROM_CART_UA = 'remove from cart';
const LOGGED_IN = 'login';
const GUEST_USER = 'guest';
const STANDARD_PAYMENT = 'standard';
const BOLT_PAYMENT = 'bolt';
const PAYPAL_PAYMENT = 'paypal';
const BEGIN_STEP_NAME = 'begin checkout';
const BEGIN_EVENT_NAME = 'begin_checkout';
const CONTACT_STEP_NAME = 'contact info';
const CONTACT_EVENT_NAME = 'contact_info';
const SHIPPING_STEP_NAME = 'shipping info';
const SHIPPING_EVENT_NAME = 'add_shipping_info';
const PAYMENT_STEP_NAME = 'payment info';
const PAYMENT_EVENT_NAME = 'add_payment_info';
const REVIEW_ORDER_STEP_NAME = 'review order';
const REVIEW_ORDER_EVENT_NAME = 'review_order';
const CHECKOUT_STEPS = [
    { step: 1, name: 'review cart', event: 'view_cart' },
    { step: 2, name: BEGIN_STEP_NAME, event: BEGIN_EVENT_NAME },
    { step: 3, name: CONTACT_STEP_NAME, event: CONTACT_EVENT_NAME },
    { step: 4, name: SHIPPING_STEP_NAME, event: SHIPPING_EVENT_NAME },
    { step: 5, name: PAYMENT_STEP_NAME, event: PAYMENT_EVENT_NAME },
    { step: 6, name: REVIEW_ORDER_STEP_NAME, event: REVIEW_ORDER_EVENT_NAME }
];
const CHECKBOX_CHECKED = 'check';
const CHECKBOX_UNCHECKED = 'uncheck';

let checkoutMethod = '';

/**
 * Gets the index for the CHECKOUT_STEPS array
 * @param {HTMLElement} node DOM node containing data-checkout-step
 * @returns {number} number indicating the current checkout step, -1 if the step could not be determined.
 */
function getCheckoutStep(node) {
    if (node && node.dataset.checkoutStage) {
        switch (node.dataset.checkoutStage) {
            case 'customer':
                return 1;
            case 'shipping':
                return 2;
            case 'payment':
                return 3;
            case 'placeOrder':
                return 4;
            default:
                return -1;
        }
    }

    return -1;
}

/**
 * Sends the GA request for checkout virtual page views
 * @param {number} stepIndex step index to be looked up in CHECKOUT_STEPS
 */
function sendCheckoutViewEvent(stepIndex) {
    /* eslint-disable camelcase */
    if (stepIndex >= 0) {
        const { step, name, event } = CHECKOUT_STEPS[stepIndex];

        if (uData) {
            const {
                product_color,
                product_id,
                product_name,
                product_category,
                product_original_price,
                product_price,
                product_quantity,
                product_size,
                product_sku,
                product_variant,
                product_is_preorder,
                user_authenticated,
                product_list_category,
                product_promo_name,
                product_promo_creative
            } = uData;

            let eLabel = name;
            let checkoutType = '';
            if (step === 3) {
                if (user_authenticated === 'true') {
                    checkoutType = LOGGED_IN;
                } else {
                    checkoutType = GUEST_USER;
                }
            }

            // GA4
            sendEventGA(
                normalizePayload({
                    virtual_page_path: `/checkout/${name}`,
                    checkout_step_number: step,
                    checkout_step_name: eLabel,
                    checkout_method: checkoutMethod,
                    checkout_type: checkoutType,
                    event_name: event,
                    ecommerce_action: CHECKOUT,
                    event_category: ECOMMERCE,
                    event_action: CHECKOUT,
                    event_label: eLabel,
                    product_color: product_color || [],
                    product_id: product_id || [],
                    product_name: product_name || [],
                    product_category: product_category || [],
                    product_original_price: product_original_price || [],
                    product_price: product_price || [],
                    product_quantity: product_quantity || [],
                    product_size: product_size || [],
                    product_sku: product_sku || [],
                    product_variant: product_variant || [],
                    product_is_preorder: product_is_preorder || [],
                    product_list_category: product_list_category || [],
                    promo_name: product_promo_name || [],
                    promo_creative: product_promo_creative || []
                })
            );
        }
    }
}

// Variable checkoutMethod is defined at the top of the file
/**
 * Gets the checkout method
 * @param {HTMLElement} node DOM node containing data-checkout-step
 * @returns {string} payment method
 */
function getCheckoutMethods(node) {
    if (node) {
        const paymentMethodEl = queryFirst('.billing-nav.payment-information', node);
        if (paymentMethodEl && paymentMethodEl.dataset) {
            const { paymentMethodId } = paymentMethodEl.dataset;
            if (paymentMethodId === 'BOLT_PAY') {
                checkoutMethod = BOLT_PAYMENT;
            } else if (paymentMethodId === 'PayPal') {
                checkoutMethod = PAYPAL_PAYMENT;
            } else {
                checkoutMethod = STANDARD_PAYMENT;
            }
        }
    }
    return checkoutMethod;
}

/**
 * Sends checkout view events on pageload/virtual page load
 */
function sendCheckoutView() {
    const pageAction = getNestedValue(window, 'utag_data.action');
    let stepIndex;

    if (pageAction) {
        if (pageAction === 'Cart-Show') {
            stepIndex = 0;
        } else if (pageAction === 'Checkout-Begin') {
            const checkoutStage = queryFirst('.data-checkout-stage');
            let stageBeforeChange = getCheckoutStep(checkoutStage);

            const checkoutStepObserver = new MutationObserver(mutations => {
                for (let mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-checkout-stage') {
                        // the node is added and removed every time the button is clicked/the PayPal window is closed
                        // trigger event only when we go from top to down
                        stepIndex = getCheckoutStep(mutation.target);
                        if (stepIndex >= stageBeforeChange) {
                            getCheckoutMethods(mutation.target);
                            sendCheckoutViewEvent(stepIndex);
                        }
                        stageBeforeChange = stepIndex;
                    }
                }
            });

            // This was previously introducted in PT-9220 to prevent an incorrect drop-off in metrics due to logged-in
            // users bypassing the login page (checkout step 2).
            // With the SFRA upgrade, login moved from a dedicated page to a step in checkout (DIP-6040).
            // As a result, I've updated this to ALWAYS send checkout step 2 on load of checkout, EXCEPT after a user
            // logs in during checkout, as that causes a page refresh and would send both an authentication event and
            // a prior authentication event (PT-10166).
            if (document.referrer.includes('stage') && !document.referrer.includes('stage=customer')) {
                sendCheckoutViewEvent(2);
            } else {
                const customerType = queryFirst('#checkout-main');
                const $body = $('body');
                getCheckoutMethods(customerType);
                // This is added to trigger all the events if the user lands directly on any of the other
                // steps in case BOLT or Paypal
                const sendSkippedStages = () => {
                    const currentStage = document.location.search;
                    if (currentStage.includes('stage=payment')) {
                        sendCheckoutViewEvent(2);
                    } else if (currentStage.includes('stage=placeOrder')) {
                        sendCheckoutViewEvent(1);
                        sendCheckoutViewEvent(2);
                        sendCheckoutViewEvent(3);
                    }

                    $body.off('checkout:stageUpdate.gaCheckout');
                };

                const currentStage = document.location.search;
                if (currentStage.includes('stage')) {
                    sendSkippedStages();
                } else {
                    $body.on('checkout:stageUpdate.gaCheckout', sendSkippedStages);
                }
            }

            checkoutStepObserver.observe(checkoutStage, { attributes: true });

            stepIndex = getCheckoutStep(checkoutStage);
        }

        if (pageAction !== 'Checkout-Begin') {
            // Mutation observer fires on pageload for Checkout-Begin -- this prevents duplicate events
            sendCheckoutViewEvent(stepIndex);
        }
    }
}

/**
 * Analytics for Cart & Checkout
 */
function initEvents() {
    const { body } = document;

    sendCheckoutView();

    // Checkout - Payment Method - PayPal
    // PayPal button exists within an external iframe
    // the only way to track it is either on window blur/checking current focus element or by listening for the element that PayPal adds to the DOM
    const paypalObserver = new MutationObserver(mutations => {
        for (let mutation of mutations) {
            if (mutation.type === 'childList' && mutation.addedNodes) {
                // the node is added and removed every time the button is clicked/the PayPal window is closed
                for (let node of mutation.addedNodes) {
                    if (hasClass(node, 'paypal-checkout-sandbox')) {
                        // this class name is the same in production as well
                        sendEventGA(
                            normalizePayload({
                                event_category: ECOMMERCE,
                                event_action: CHECKOUT_OPTION,
                                event_label: 'PayPal',

                                ecommerce_action: CHECKOUT,
                                checkout_option: 'PayPal',

                                // GA4
                                checkout_step_number: 2
                            })
                        );
                    }
                }
            }
        }
    });

    paypalObserver.observe(document.body, { childList: true });

    // GA4- Checkout- Bolt Sign Out
    bindGA(
        queryFirst('.bolt-sign-out'),
        () => {
            return {
                event_name: BOLT_INTERACTION,
                interaction_type: BOLT_SIGN_OUT
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.logout-button-bolt']
            }
        }
    );

    // Checkout - Login Method - Register
    bindGA(
        body,
        {
            event_category: ECOMMERCE,
            event_action: CHECKOUT_OPTION,
            event_label: 'create account now',

            ecommerce_action: CHECKOUT,
            checkout_option: 'create account now',
            checkout_step: 3,

            // GA4
            checkout_step_number: 3,
            checkout_type: LOGGED_IN,
            checkout_step_name: CONTACT_STEP_NAME,
            event_name: CONTACT_EVENT_NAME,
            checkout_method: STANDARD_PAYMENT
        },
        {
            eventType: 'register:success',
            isCustomEvent: true,
            retriggerEvent: false
        }
    );

    // Shipping Method
    bindGA(
        body,
        (bindElement, srcElement) => {
            // There is a triggered click on the button after validation which causes a duplicate event
            if (hasClass(srcElement, 'moveToNextStep')) {
                return false;
            }

            const shippingOption = queryFirst('.shipping-method input:checked');
            const shippingMethod = shippingOption ? queryFirst('.display-name', shippingOption.closest('.custom-radio')).textContent.trim() : 'in-store pickup';

            return {
                event_category: ECOMMERCE,
                event_action: CHECKOUT_OPTION,
                event_label: shippingMethod,

                ecommerce_action: CHECKOUT,
                checkout_option: shippingMethod,
                checkout_step: 4
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.submit-shipping']
            }
        }
    );

    // Payment Method
    bindGA(
        body,
        () => {
            const paymentMethod = queryFirst('.payment-options .active').closest('.nav-item').dataset.methodName;
            const customerType = document.getElementById('checkout-main');
            // For signed-in users, new or saved card?
            if (customerType && customerType.dataset.customerType === 'registered') {
                const selectedCard = queryFirst('#paymentSelector option:checked');
                const isNewCard = !selectedCard || selectedCard.value === 'new';

                sendEventGA({
                    event_category: ECOMMERCE,
                    event_action: isNewCard ? 'new credit card' : 'saved credit card',
                    event_label: CHECKOUT
                });
            }

            return {
                event_category: ECOMMERCE,
                event_action: CHECKOUT_OPTION,
                event_label: paymentMethod,

                ecommerce_action: CHECKOUT,
                checkout_option: paymentMethod,
                checkout_step: 5
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.submit-payment']
            }
        }
    );

    // Place Order
    bindGA(
        body,
        () => {
            const customerType = document.getElementById('checkout-main');
            // For signed-in users, new or saved card?
            const boltCheckbox = queryFirst('#acct-checkbox #bolt-acct-check');

            const eventObject = {
                // GA4
                event_name: REVIEW_ORDER_EVENT_NAME,
                checkout_step_number: 6,
                checkout_step_name: REVIEW_ORDER_STEP_NAME,
                checkout_method: getCheckoutMethods(customerType)
            };

            // GA4 event to track Bolt account optin by non-bolt user
            if (boltCheckbox) {
                const isSaveToBoltSelected = Boolean(boltCheckbox && boltCheckbox.checked);
                eventObject.bolt_save_info = isSaveToBoltSelected.toString();
            }

            return eventObject;
        },
        {
            bindOptions: {
                targetSelectors: ['#submit-order']
            }
        }
    );

    // Gift Card Apply
    bindGA(
        body,
        {
            event_category: PAYMENT_METHOD,
            event_action: 'giftcard'
        },
        {
            bindOptions: {
                targetSelectors: ['.apply-balance']
            }
        }
    );

    // Promo Codes
    bindGA(
        queryAll('.promo-code-form'),
        bindElement => {
            const promoCode = queryFirst('.coupon-code-field', bindElement);

            return {
                event_category: SUBMISSIONS,
                event_action: 'promo code submit',
                event_label: (promoCode && promoCode.value) || ''
            };
        },
        {
            eventType: 'submit',
            retriggerEvent: false
        }
    );

    bindGA(body, (bindElement, srcElement) => {
        const { name, pid, tealium } = srcElement.dataset;
        let productData = {};

        try {
            productData = JSON.parse(tealium);
        } catch (e) {
            // JSON.parse failed
        }

        return {
            event_name: REMOVE_FROM_CART,
            event_category: ECOMMERCE,
            event_action: REMOVE_FROM_CART_UA,
            event_label: [name, pid],
            ...productData
        };
    }, {
        bindOptions: {
            targetSelectors: ['.remove-product']
        }
    });

    // Update Cart Quantity
    bindGA(
        body,
        (bindElement, srcElement, e) => {
            const { oldQty, newQty, analyticsData } = e.data;
            const eventName = oldQty < newQty ? ADD_TO_CART : REMOVE_FROM_CART;
            let productData = {};
            const eventQty = Math.abs(newQty - oldQty);

            try {
                productData = JSON.parse(analyticsData);
                productData.product_quantity = [eventQty];
            } catch (e) {
                // JSON.parse failed
            }

            return {
                event_name: eventName,
                ...productData
            };
        },
        {
            eventType: 'cart:qtyUpdate'
        }
    );

    // Remove from SFL / Move to Tote from SFL
    bindGA(
        body,
        (bindElement, srcElement) => {
            const { name, masterid, color, tealium } = srcElement.dataset;
            let productData = {};
            const isMoveToTote = hasClass(srcElement, 'js-move-sfl-to-tote');

            try {
                productData = JSON.parse(tealium);
            } catch (e) {
                // JSON.parse failed
            }

            return {
                event_category: ECOMMERCE,
                event_action: isMoveToTote ? ADD_TO_CART_UA : REMOVE_FROM_CART_UA,
                event_label: [name, masterid, color],
                ...productData
            };
        },
        {
            usesContext: true,
            bindOptions: {
                targetSelectors: ['.js-remove-sfl-product', '.js-move-sfl-to-tote']
            }
        }
    );

    // Move to SFL
    bindGA(
        body,
        (bindElement, srcElement) => {
            const { name, pid } = srcElement.dataset;

            return {
                event_category: ECOMMERCE,
                event_action: 'save_for_later',
                event_label: [name, pid]
            };
        },
        {
            usesContext: true,
            bindOptions: {
                targetSelectors: ['.js-move-to-sfl']
            }
        }
    );

    // Move ALL to tote from SFL
    bindGA(
        body,
        {
            event_category: ECOMMERCE,
            event_action: ADD_TO_CART_UA,
            event_label: 'move all items'
        },
        {
            usesContext: true,
            bindOptions: {
                targetSelectors: ['.move-all-to-tote']
            }
        }
    );

    // Tote/SFL tabs
    bindGA(
        body,
        (bindElement, srcElement, e) => {
            const { fromTab, toTab } = e.data;

            return {
                event_category: 'my tote tabs',
                event_action: 'click',
                event_label: toTab,
                // page_subsection set directly instead of via usesContext because we want to set the previously selected tab here.
                page_subsection: fromTab
            };
        },
        {
            eventType: 'tote:selectTab',
            isCustomEvent: true
        }
    );

    // SFL Login
    bindGA(
        body,
        {
            event_category: 'account sign in',
            event_action: 'click',
            event_label: 'start sign in'
        },
        {
            usesContext: true,
            bindOptions: {
                targetSelectors: ['.sfl-login-wrapper .account-login-btn']
            }
        }
    );

    // Move Product to Wishlist
    bindGA(queryAll('.js-move-to-wishlist'), bindElement => {
        const { name, pid } = bindElement.dataset;

        return {
            event_category: ECOMMERCE,
            event_action: 'move to wishlist',
            event_label: [name, pid]
        };
    });

    // Pick Up In Store/Ship to Home Links
    bindGA(
        body,
        (bindElement, srcElement) => {
            const { productName, pid } = srcElement.dataset;
            const eAction = hasClass(srcElement, 'btn-in-store-pickup') ? 'store pick up' : 'ship to home';

            return {
                event_category: ECOMMERCE,
                event_action: eAction,
                event_label: [productName, pid]
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.btn-in-store-pickup', '.js-move-to-shipping']
            }
        }
    );

    // BOPIS Cart "Add to Tote for Pick Up" confirmation button
    bindGA(
        body,
        (bindElement, srcElement) => {
            const drawerContainer = srcElement.closest('.pickup-in-store-container');
            if (drawerContainer) {
                const { productName, swatchName, masterId } = drawerContainer.dataset;

                return {
                    event_category: ECOMMERCE,
                    event_action: ADD_TO_CART,
                    event_label: [productName, masterId, swatchName]
                };
            }
        },
        {
            bindOptions: {
                targetSelectors: ['.store-confirm-button']
            }
        }
    );

    // add to tote gift card on cart
    bindGA(
        queryFirst('.cart-gift-card'),
        (bindElement, srcElement) => {
            const { productName, productId } = getProductData(srcElement);
            const selectedValue = queryFirst('.selected-value span');
            const productPrice = selectedValue ? getText(selectedValue) : '';
            return {
                // GA4
                event_name: 'add_to_cart',
                product_id: productId,
                product_name: productName,
                product_price: productPrice,
                page_name: uData.page_name,
                page_type: uData.page_type
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.gift-card-add-to-cart-btn']
            }
        }
    );

    bindGA(
        body,
        (bindElement, srcElement) => {
            return {
                // GA360
                event_category: LOYALTY_REWARDS,
                event_action: hasClass(srcElement, 'collapsed') ? EXPAND_BTN : COLLAPSE_BTN,
                event_label: APPLY_REWARDS,

                // GA4
                event_name: LOYALTY_REWARDS_EVENT_NAME,
                interaction_type: hasClass(srcElement, 'collapsed') ? EXPAND_BTN : COLLAPSE_BTN,
                page_name: uData.page_name,
                page_type: uData.page_type,
                page_section: LOYALTY_REWARDS,
                loyalty_status: uData.loyalty_status
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.vouchers-accordion-container .btn-link']
            }
        }
    );

    bindGA(
        body,
        (bindElement, srcElement) => {
            const voucherContainer = srcElement.closest('.voucher-card-content');
            const rewardName = queryFirst('.card-off-purchase', voucherContainer);
            return {
                // GA360
                event_category: LOYALTY_REWARDS,
                event_action: APPLY,
                event_label: rewardName.textContent.trim(),

                // GA4
                event_name: LOYALTY_REWARDS_EVENT_NAME,
                interaction_type: APPLY,
                loyalty_reward_name: rewardName.textContent.trim(),
                page_name: uData.page_name,
                page_type: uData.page_type,
                page_section: LOYALTY_REWARDS,
                loyalty_status: uData.loyalty_status
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.apply-voucher-tote-cta']
            }
        }
    );

    bindGA(
        body,
        (bindElement, srcElement) => {
            const voucherContainer = srcElement.closest('.voucher-card-content');
            const rewardName = queryFirst('.card-off-purchase', voucherContainer);
            return {
                // GA360
                event_category: LOYALTY_REWARDS,
                event_action: REMOVE,
                event_label: rewardName.textContent.trim(),

                // GA4
                event_name: LOYALTY_REWARDS_EVENT_NAME,
                interaction_type: REMOVE,
                loyalty_reward_name: rewardName.textContent.trim(),
                page_name: uData.page_name,
                page_type: uData.page_type,
                page_section: LOYALTY_REWARDS,
                loyalty_status: uData.loyalty_status
            };
        },
        {
            bindOptions: {
                targetSelectors: ['.remove-voucher-tote-cta']
            }
        }
    );

    // GA event triggers when user enters correct OTP in bolt OTP modal and proceeds further
    bindGA(
        body,
        () => {
            return {
                // GA4
                event_name: BOLT_INTERACTION,
                interaction_type: BOLT_MODAL_PROCEED,
                checkout_method: BOLT_PAYMENT
            };
        },
        {
            eventType: 'bolt:proceedOtpModal'
        }
    );

    // GA event triggers when user closes the bolt OTP modal
    bindGA(
        body,
        () => {
            return {
                // GA4
                event_name: BOLT_INTERACTION,
                interaction_type: BOLT_MODAL_CLOSE
            };
        },
        {
            eventType: 'bolt:closeOtpModal'
        }
    );
}

initEvents();
